// Annual Vendor Audit — the audit detail drawer, deep-linkable via
// ?audit=<id> from the registry page and from the vendor record's Annual
// audit tab. One surface for the whole lifecycle:
//
//   draft → in-vendor-review → awaiting-final-assessment → completed
//                                                       ↘ superseded
//
// Which actions exist is a function of the status, and only of the status —
// a completed audit is READ-ONLY here by design. It was released to the
// vendor, who commented on the numbers in front of them, so rewriting it
// would leave their replies attached to a document that no longer says what
// they replied to. The correction path is Supersede, which retires this row
// and opens a fresh draft pointing back at it.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  ExceptionBadge,
  Input,
  StatusBadge,
  Textarea,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCalendarDate } from "@/design-system/lib/format";
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { useDateFormatter } from "@/utils/datetime";
import {
  VENDOR_AUDIT_REVIEW_WINDOW,
  vendorAuditsApi,
  type VendorAuditDetermination,
  type VendorAuditQuoteDto,
  type VendorAuditRecommendation,
  type VendorAuditStatus,
} from "@/api/portfolioAdmin/vendorAuditsApi";
import {
  useAddVendorAuditComment,
  useAddVendorAuditQuote,
  useCloseVendorAuditReview,
  useCompleteVendorAudit,
  useReleaseVendorAudit,
  useRemoveVendorAuditQuote,
  useSupersedeVendorAudit,
  useUpdateVendorAuditDraft,
  useUpdateVendorAuditQuote,
  useVendorAudit,
} from "@/hooks/portfolioAdmin/useVendorAudits";

// ── the shared vocabulary ────────────────────────────────────────────────
// Exported so the registry page and the vendor tab say the same words in the
// same colours. Same condition, same colour everywhere (the
// vendorReportFormat convention).

export const VENDOR_AUDIT_STATUS_LABEL: Record<VendorAuditStatus, string> = {
  draft: "Draft",
  "in-vendor-review": "In vendor review",
  "awaiting-final-assessment": "Awaiting determination",
  completed: "Completed",
  superseded: "Superseded",
};

export function vendorAuditStatusTone(status: VendorAuditStatus): StatusTone {
  switch (status) {
    case "in-vendor-review":
      return "info";
    case "awaiting-final-assessment":
      return "progress";
    case "completed":
      return "ok";
    case "superseded":
      return "muted";
    default:
      return "neutral";
  }
}

export const VENDOR_AUDIT_DETERMINATION_LABEL: Record<
  VendorAuditDetermination,
  string
> = {
  competitive: "Competitive",
  "not-competitive": "Not competitive",
  inconclusive: "Inconclusive",
};

/** Inconclusive is NEUTRAL, not a warning. "We could not compare" is an
 *  honest answer — usually because competitors declined to quote — and
 *  colouring it amber would read as a problem with the vendor. */
export function vendorAuditDeterminationTone(
  determination: VendorAuditDetermination,
): StatusTone {
  switch (determination) {
    case "competitive":
      return "ok";
    case "not-competitive":
      return "danger";
    default:
      return "neutral";
  }
}

export const VENDOR_AUDIT_RECOMMENDATION_LABEL: Record<
  VendorAuditRecommendation,
  string
> = {
  retain: "Retain",
  renegotiate: "Renegotiate",
  replace: "Replace",
};

/**
 * The vendor's comment window, as an exception or as prose.
 *
 * Amber means "needs a look this week", red means "the date has passed" —
 * two different things, per the badge vocabulary. `daysRemaining` is floored
 * at 0 server-side, so ELAPSED is derived from `reviewClosesAt` rather than
 * from a negative that never arrives.
 */
export function vendorAuditReviewClock(
  audit: {
    status: VendorAuditStatus;
    reviewClosesAt: string | null;
    daysRemaining: number | null;
  },
  now: number = Date.now(),
): { severity: "attention" | "overdue" | null; label: string } | null {
  if (audit.status !== "in-vendor-review" || !audit.reviewClosesAt) return null;
  const closes = new Date(audit.reviewClosesAt).getTime();
  if (Number.isNaN(closes)) return null;
  if (closes <= now) {
    return { severity: "overdue", label: "Comment window elapsed" };
  }
  const days = audit.daysRemaining ?? 0;
  const label = days === 1 ? "1 day left" : `${days} days left`;
  return { severity: days <= 7 ? "attention" : null, label };
}

const DETERMINATIONS: VendorAuditDetermination[] = [
  "competitive",
  "not-competitive",
  "inconclusive",
];

const RECOMMENDATIONS: VendorAuditRecommendation[] = [
  "retain",
  "renegotiate",
  "replace",
];

const SELECT_CLASS =
  "h-10 w-full px-space-2 border border-border-light rounded-md text-small bg-white-pure";

type ModalMode =
  | null
  | "release"
  | "close-review"
  | "complete"
  | "supersede"
  | "quote"
  | "remove-quote";

interface QuoteForm {
  id: string | null;
  competitorName: string;
  contactName: string;
  contactMethod: string;
  contactedOn: string;
  quotedTerms: string;
  servicesOffered: string;
  declined: boolean;
}

const EMPTY_QUOTE: QuoteForm = {
  id: null,
  competitorName: "",
  contactName: "",
  contactMethod: "",
  contactedOn: "",
  quotedTerms: "",
  servicesOffered: "",
  declined: false,
};

function quoteToForm(quote: VendorAuditQuoteDto): QuoteForm {
  return {
    id: quote.id,
    competitorName: quote.competitorName,
    contactName: quote.contactName ?? "",
    contactMethod: quote.contactMethod ?? "",
    // A contact date is a wall-calendar value; the stored instant is noon UTC,
    // so its UTC date IS the day, and slicing avoids the tz shift a local
    // `toISOString()` on a parsed Date would reintroduce.
    contactedOn: quote.contactedOn ? quote.contactedOn.slice(0, 10) : "",
    quotedTerms: quote.quotedTerms ?? "",
    servicesOffered: quote.servicesOffered ?? "",
    declined: quote.declined,
  };
}

/** `""` clears the field server-side, `null` leaves it unchanged — we always
 *  send the full form, so a blanked box must arrive as `""`. */
function orBlank(value: string): string {
  return value.trim();
}

interface VendorAuditDrawerProps {
  auditId: string;
  open: boolean;
  onClose: () => void;
  /** Superseding retires this audit and opens a NEW draft. Without this the
   *  reader is left looking at the row they just retired with no route to the
   *  correction they asked for. */
  onReplaced?: (auditId: string) => void;
}

export function VendorAuditDrawer({
  auditId,
  open,
  onClose,
  onReplaced,
}: VendorAuditDrawerProps) {
  const toast = useToast();
  const auth = useAuth();
  const { date } = useDateFormatter();
  const auditQuery = useVendorAudit(auditId);
  const audit = auditQuery.data;

  const updateDraft = useUpdateVendorAuditDraft(auditId);
  const addQuote = useAddVendorAuditQuote(auditId);
  const updateQuote = useUpdateVendorAuditQuote(auditId);
  const removeQuote = useRemoveVendorAuditQuote(auditId);
  const release = useReleaseVendorAudit(auditId);
  const closeReview = useCloseVendorAuditReview(auditId);
  const complete = useCompleteVendorAudit(auditId);
  const supersede = useSupersedeVendorAudit(auditId);
  const addComment = useAddVendorAuditComment(auditId);

  const [modal, setModal] = useState<ModalMode>(null);
  const [pricing, setPricing] = useState("");
  const [services, setServices] = useState("");
  const [finding, setFinding] = useState("");
  const [windowDays, setWindowDays] = useState(
    String(VENDOR_AUDIT_REVIEW_WINDOW.defaultDays),
  );
  const [determination, setDetermination] = useState<VendorAuditDetermination | "">("");
  const [recommendation, setRecommendation] = useState<VendorAuditRecommendation | "">("");
  const [narrative, setNarrative] = useState("");
  const [reply, setReply] = useState("");
  const [quoteForm, setQuoteForm] = useState<QuoteForm>(EMPTY_QUOTE);
  const [quoteToRemove, setQuoteToRemove] = useState<VendorAuditQuoteDto | null>(null);

  // Hydrate ONCE per audit, keyed on the id rather than on the object. A
  // refetch after any save returns a new object; re-hydrating on that would
  // stomp whatever the reader had typed since.
  useEffect(() => {
    if (!audit) return;
    setPricing(audit.phoenixPricingSummary ?? "");
    setServices(audit.phoenixServicesSummary ?? "");
    setFinding(audit.preliminaryFinding ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audit?.id]);

  const canManage =
    auth.hasPermission("Vendors.Manage") || auth.hasRoleCode("ADMIN");
  const status = audit?.status;
  const isDraft = status === "draft";
  const isInReview = status === "in-vendor-review";
  const isAwaiting = status === "awaiting-final-assessment";
  const isCompleted = status === "completed";
  const clock = audit ? vendorAuditReviewClock(audit) : null;

  const closeModal = () => {
    setModal(null);
    setQuoteToRemove(null);
  };

  const run = async (action: () => Promise<unknown>, message: string) => {
    try {
      await action();
      toast.success(message);
      closeModal();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const onSaveDraft = () =>
    run(
      () =>
        updateDraft.mutateAsync({
          phoenixPricingSummary: orBlank(pricing),
          phoenixServicesSummary: orBlank(services),
          preliminaryFinding: orBlank(finding),
        }),
      "Draft saved.",
    );

  const onSubmitQuote = () =>
    run(async () => {
      const body = {
        competitorName: quoteForm.competitorName.trim(),
        contactName: orBlank(quoteForm.contactName),
        contactMethod: orBlank(quoteForm.contactMethod),
        // Noon UTC, so the day survives every viewer's timezone.
        contactedOn: quoteForm.contactedOn
          ? `${quoteForm.contactedOn}T12:00:00.000Z`
          : "",
        quotedTerms: orBlank(quoteForm.quotedTerms),
        servicesOffered: orBlank(quoteForm.servicesOffered),
        declined: quoteForm.declined,
      };
      if (quoteForm.id) {
        await updateQuote.mutateAsync({ quoteId: quoteForm.id, body });
      } else {
        await addQuote.mutateAsync(body);
      }
    }, quoteForm.id ? "Competitor quote updated." : "Competitor quote recorded.");

  const onSupersede = async () => {
    try {
      const replacement = await supersede.mutateAsync();
      toast.success("Superseded — a correction draft has been opened.");
      closeModal();
      onReplaced?.(replacement.id);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const windowDaysValue = Number(windowDays);
  const windowDaysValid =
    Number.isInteger(windowDaysValue) &&
    windowDaysValue >= VENDOR_AUDIT_REVIEW_WINDOW.minDays &&
    windowDaysValue <= VENDOR_AUDIT_REVIEW_WINDOW.maxDays;

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      width="lg"
      title="Annual audit"
      subtitle={audit ? `${audit.vendorName} · ${audit.auditYear}` : undefined}
      testId="vendor-audit-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {canManage && audit && isDraft && (
            <Button
              variant="primary"
              onClick={() => setModal("release")}
              data-testid="vendor-audit-release-button"
            >
              Release to vendor…
            </Button>
          )}
          {canManage && audit && isInReview && (
            <Button
              variant="primary"
              onClick={() => setModal("close-review")}
              data-testid="vendor-audit-close-review-button"
            >
              Close comment window early…
            </Button>
          )}
          {canManage && audit && isAwaiting && (
            <Button
              variant="primary"
              onClick={() => setModal("complete")}
              data-testid="vendor-audit-complete-button"
            >
              Record determination…
            </Button>
          )}
          {canManage && audit && isCompleted && (
            <Button
              variant="secondary"
              onClick={() => setModal("supersede")}
              data-testid="vendor-audit-supersede-button"
            >
              Supersede…
            </Button>
          )}
        </>
      }
    >
      {auditQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading audit…</p>
      )}
      {auditQuery.isError && (
        <p className="text-small text-status-error m-0" role="alert">
          Could not load this audit. Refresh to retry.
        </p>
      )}

      {audit && (
        <div className="flex flex-col gap-space-4">
          {/* ── status line ─────────────────────────────────────────── */}
          <section className="flex flex-wrap items-center gap-space-2">
            <StatusBadge tone={vendorAuditStatusTone(audit.status)}>
              {VENDOR_AUDIT_STATUS_LABEL[audit.status]}
            </StatusBadge>
            {clock &&
              (clock.severity ? (
                <ExceptionBadge
                  severity={clock.severity}
                  className="ml-0"
                  data-testid="vendor-audit-review-clock"
                >
                  {clock.label}
                </ExceptionBadge>
              ) : (
                <span
                  className="text-caption text-gray-mid"
                  data-testid="vendor-audit-review-clock"
                >
                  {clock.label}
                </span>
              ))}
            <Link
              to={`/portfolio-admin/vendors/${audit.vendorId}?tab=annual-audit`}
              className="text-small text-gold-shadow hover:underline"
              data-testid="vendor-audit-vendor-link"
            >
              {audit.vendorName}
            </Link>
          </section>

          {audit.supersedesId && (
            <p
              className="text-caption text-gray-mid italic m-0"
              data-testid="vendor-audit-supersedes-note"
            >
              This audit corrects an earlier {audit.auditYear} audit. The
              original is retained unchanged.
            </p>
          )}

          {/* ── Phoenix's side ──────────────────────────────────────── */}
          <section>
            <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-2">
              <h3 className="font-display font-semibold text-h3 m-0">
                What Phoenix pays and receives
              </h3>
              {canManage && isDraft && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void onSaveDraft()}
                  disabled={updateDraft.isPending}
                  data-testid="vendor-audit-save-draft"
                >
                  Save draft
                </Button>
              )}
            </div>
            {isDraft ? (
              <div className="flex flex-col gap-space-3">
                <p className="text-caption text-gray-mid m-0">
                  Typed by you, never pulled from contract terms or invoice
                  history — a market check is an assertion someone makes and
                  stands behind.
                </p>
                <DraftField
                  label="Phoenix pricing today"
                  value={pricing}
                  onChange={setPricing}
                  disabled={!canManage}
                  testId="vendor-audit-pricing"
                />
                <DraftField
                  label="Services Phoenix receives"
                  value={services}
                  onChange={setServices}
                  disabled={!canManage}
                  testId="vendor-audit-services"
                />
                <DraftField
                  label="Preliminary finding"
                  value={finding}
                  onChange={setFinding}
                  disabled={!canManage}
                  testId="vendor-audit-finding"
                />
              </div>
            ) : (
              <dl className="m-0 flex flex-col gap-space-3">
                <ReadField label="Phoenix pricing today" value={audit.phoenixPricingSummary} />
                <ReadField label="Services Phoenix receives" value={audit.phoenixServicesSummary} />
                <ReadField label="Preliminary finding" value={audit.preliminaryFinding} />
              </dl>
            )}
          </section>

          {/* ── competitor quotes ───────────────────────────────────── */}
          <section>
            <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-2">
              <h3 className="font-display font-semibold text-h3 m-0">
                Competitors approached ({audit.quotes.length})
              </h3>
              {canManage && isDraft && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setQuoteForm(EMPTY_QUOTE);
                    setModal("quote");
                  }}
                  data-testid="vendor-audit-add-quote"
                >
                  Add competitor
                </Button>
              )}
            </div>
            {audit.quotes.length === 0 ? (
              <p className="text-small text-gray-mid m-0" data-testid="vendor-audit-quotes-empty">
                No competitors recorded yet. A competitor who would not quote is
                still worth recording — that is the evidence behind an
                inconclusive determination.
              </p>
            ) : (
              <ul
                className="m-0 p-0 list-none flex flex-col gap-space-2"
                data-testid="vendor-audit-quotes"
              >
                {audit.quotes.map((quote) => (
                  <li
                    key={quote.id}
                    className="rounded-md border border-border-light p-space-3"
                    data-testid={`vendor-audit-quote-${quote.id}`}
                  >
                    <div className="flex items-start justify-between gap-space-2 flex-wrap">
                      <span className="text-small font-medium">
                        {quote.competitorName}
                        {quote.declined && (
                          <ExceptionBadge severity="attention">
                            Declined to quote
                          </ExceptionBadge>
                        )}
                      </span>
                      {canManage && isDraft && (
                        <span className="flex items-center gap-space-2">
                          <Button
                            variant="text"
                            size="sm"
                            className="px-0"
                            onClick={() => {
                              setQuoteForm(quoteToForm(quote));
                              setModal("quote");
                            }}
                            data-testid={`vendor-audit-edit-quote-${quote.id}`}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="text"
                            size="sm"
                            className="px-0"
                            onClick={() => {
                              setQuoteToRemove(quote);
                              setModal("remove-quote");
                            }}
                            data-testid={`vendor-audit-remove-quote-${quote.id}`}
                          >
                            Remove
                          </Button>
                        </span>
                      )}
                    </div>
                    <p className="text-caption text-gray-mid m-0 mt-space-1">
                      {[
                        quote.contactName,
                        quote.contactMethod,
                        quote.contactedOn
                          ? `contacted ${formatCalendarDate(quote.contactedOn)}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "No contact details recorded"}
                    </p>
                    {quote.quotedTerms && (
                      <p className="text-small m-0 mt-space-2 whitespace-pre-wrap">
                        <span className="text-gray-mid">Quoted: </span>
                        {quote.quotedTerms}
                      </p>
                    )}
                    {quote.servicesOffered && (
                      <p className="text-small m-0 mt-space-1 whitespace-pre-wrap">
                        <span className="text-gray-mid">Includes: </span>
                        {quote.servicesOffered}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* ── determination ───────────────────────────────────────── */}
          {(isCompleted || audit.status === "superseded") &&
            audit.finalDetermination && (
              <section data-testid="vendor-audit-determination">
                <h3 className="font-display font-semibold text-h3 m-0 mb-space-2">
                  Determination
                </h3>
                <div className="flex flex-wrap items-center gap-space-2 mb-space-2">
                  <StatusBadge
                    tone={vendorAuditDeterminationTone(audit.finalDetermination)}
                  >
                    {VENDOR_AUDIT_DETERMINATION_LABEL[audit.finalDetermination]}
                  </StatusBadge>
                  {audit.finalRecommendation && (
                    <span className="text-caption text-gray-mid">
                      Recommendation:{" "}
                      {VENDOR_AUDIT_RECOMMENDATION_LABEL[audit.finalRecommendation]}
                    </span>
                  )}
                </div>
                {audit.finalNarrative && (
                  <p className="text-small m-0 whitespace-pre-wrap rounded-md bg-white-warm p-space-3">
                    {audit.finalNarrative}
                  </p>
                )}
                <p className="text-caption text-gray-mid m-0 mt-space-2">
                  {audit.completedAt ? `Recorded ${date(audit.completedAt)}` : ""}
                  {audit.completedByName ? ` by ${audit.completedByName}` : ""}
                  {audit.reportFiledAt
                    ? ` · Report filed ${date(audit.reportFiledAt)}`
                    : ""}
                </p>
                {/* A filed report the reader cannot open is a destination with
                    no door — the defect class this repo keeps a ratchet for.
                    Rendered only when a document was actually filed, so the
                    link can never point at nothing. The report is INTERNAL
                    (D9): the vendor never receives a copy. */}
                {audit.reportFiledAt && (
                  <p className="text-caption m-0 mt-space-1 flex gap-space-3">
                    <a
                      href={vendorAuditsApi.reportPreviewUrl(audit.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold-shadow hover:underline"
                      data-testid="vendor-audit-report-view"
                    >
                      View report
                    </a>
                    <a
                      href={vendorAuditsApi.reportDownloadUrl(audit.id)}
                      className="text-gold-shadow hover:underline"
                      data-testid="vendor-audit-report-download"
                    >
                      Download
                    </a>
                  </p>
                )}
              </section>
            )}

          {/* ── the two-sided thread ────────────────────────────────── */}
          <section>
            <h3 className="font-display font-semibold text-h3 m-0 mb-space-2">
              Vendor comments ({audit.comments.length})
            </h3>
            {audit.comments.length === 0 ? (
              <p className="text-small text-gray-mid m-0" data-testid="vendor-audit-thread-empty">
                {isDraft
                  ? "The thread opens when the audit is released to the vendor."
                  : "Nothing has been said yet."}
              </p>
            ) : (
              <ul
                className="m-0 p-0 list-none flex flex-col gap-space-2"
                data-testid="vendor-audit-thread"
              >
                {audit.comments.map((comment) => (
                  <li
                    key={comment.id}
                    className="rounded-md bg-white-warm p-space-3"
                    data-testid={`vendor-audit-comment-${comment.id}`}
                  >
                    <p className="text-caption text-gray-mid m-0 mb-space-1">
                      <span className="font-medium text-onyx">
                        {comment.authorName ??
                          (comment.authorRole === "phoenix"
                            ? "Phoenix Capital"
                            : audit.vendorName)}
                      </span>
                      {" · "}
                      {comment.authorRole === "phoenix" ? "Phoenix" : "Vendor"}
                      {" · "}
                      {date(comment.createdAt)}
                    </p>
                    <p className="text-small m-0 whitespace-pre-wrap">{comment.body}</p>
                  </li>
                ))}
              </ul>
            )}

            {canManage && isInReview && (
              <div className="mt-space-3 flex flex-col gap-space-2">
                <label className="flex flex-col gap-space-1 text-caption">
                  <span className="text-gray-mid uppercase tracking-label">
                    Reply to the vendor
                  </span>
                  <Textarea
                    rows={3}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    data-testid="vendor-audit-reply-input"
                  />
                </label>
                <span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!reply.trim() || addComment.isPending}
                    onClick={() =>
                      void run(async () => {
                        await addComment.mutateAsync({ body: reply.trim() });
                        setReply("");
                      }, "Comment added.")
                    }
                    data-testid="vendor-audit-reply-submit"
                  >
                    Post comment
                  </Button>
                </span>
              </div>
            )}
          </section>

          {/* ── history ─────────────────────────────────────────────── */}
          <section>
            <h3 className="font-display font-semibold text-h3 m-0 mb-space-2">
              History
            </h3>
            <ul
              className="m-0 p-0 list-none flex flex-col gap-space-1 text-small"
              data-testid="vendor-audit-history"
            >
              <li>Opened {date(audit.createdAt)}</li>
              {audit.releasedAt && <li>Released to vendor {date(audit.releasedAt)}</li>}
              {audit.reviewClosesAt && (
                <li>Comment window closes {date(audit.reviewClosesAt)}</li>
              )}
              {audit.reviewClosedAt && (
                <li>
                  Comment window closed {date(audit.reviewClosedAt)}
                  {audit.reviewClosedEarly ? " (closed early)" : ""}
                </li>
              )}
              {audit.completedAt && <li>Determination recorded {date(audit.completedAt)}</li>}
            </ul>
          </section>
        </div>
      )}

      {/* ── modals ────────────────────────────────────────────────── */}

      {modal === "release" && (
        <Modal
          open
          onClose={closeModal}
          title="Release to the vendor"
          description="The vendor sees this audit — your summaries, your preliminary finding and every competitor quote — and can comment on it until the window closes."
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!windowDaysValid || release.isPending}
                onClick={() =>
                  void run(
                    () => release.mutateAsync({ reviewWindowDays: windowDaysValue }),
                    "Released to the vendor for comment.",
                  )
                }
                data-testid="vendor-audit-release-confirm"
              >
                Release
              </Button>
            </>
          }
        >
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">
              Comment window (days)
            </span>
            <Input
              type="number"
              min={VENDOR_AUDIT_REVIEW_WINDOW.minDays}
              max={VENDOR_AUDIT_REVIEW_WINDOW.maxDays}
              value={windowDays}
              invalid={!windowDaysValid}
              onChange={(e) => setWindowDays(e.target.value)}
              data-testid="vendor-audit-release-days"
            />
            <span className="text-gray-mid">
              {VENDOR_AUDIT_REVIEW_WINDOW.minDays}–
              {VENDOR_AUDIT_REVIEW_WINDOW.maxDays} days. Defaults to{" "}
              {VENDOR_AUDIT_REVIEW_WINDOW.defaultDays}.
            </span>
          </label>
        </Modal>
      )}

      {modal === "close-review" && (
        <ConfirmModal
          open
          title="Close the comment window early"
          confirmLabel="Close the window"
          message={
            <>
              The vendor loses the rest of their window and can no longer
              comment. This is recorded as an early close, so the record shows
              the clock was stopped rather than run out.
            </>
          }
          onCancel={closeModal}
          onConfirm={() =>
            void run(() => closeReview.mutateAsync(), "Vendor comment window closed.")
          }
        />
      )}

      {modal === "complete" && (
        <Modal
          open
          onClose={closeModal}
          title="Record the determination"
          description="This completes the audit. A completed audit is never edited — a correction supersedes it with a fresh one."
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!determination || !recommendation || complete.isPending}
                onClick={() =>
                  void run(
                    () =>
                      complete.mutateAsync({
                        determination: determination as VendorAuditDetermination,
                        recommendation: recommendation as VendorAuditRecommendation,
                        narrative: narrative.trim() || null,
                      }),
                    "Determination recorded.",
                  )
                }
                data-testid="vendor-audit-complete-confirm"
              >
                Complete audit
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-space-3">
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Determination
              </span>
              <select
                className={SELECT_CLASS}
                value={determination}
                onChange={(e) =>
                  setDetermination(e.target.value as VendorAuditDetermination | "")
                }
                data-testid="vendor-audit-complete-determination"
              >
                <option value="">Select…</option>
                {DETERMINATIONS.map((d) => (
                  <option key={d} value={d}>
                    {VENDOR_AUDIT_DETERMINATION_LABEL[d]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Recommendation
              </span>
              <select
                className={SELECT_CLASS}
                value={recommendation}
                onChange={(e) =>
                  setRecommendation(e.target.value as VendorAuditRecommendation | "")
                }
                data-testid="vendor-audit-complete-recommendation"
              >
                <option value="">Select…</option>
                {RECOMMENDATIONS.map((r) => (
                  <option key={r} value={r}>
                    {VENDOR_AUDIT_RECOMMENDATION_LABEL[r]}
                  </option>
                ))}
              </select>
              <span className="text-gray-mid">
                Recorded only — nothing downstream acts on it.
              </span>
            </label>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Reasoning (optional)
              </span>
              <Textarea
                rows={4}
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
                data-testid="vendor-audit-complete-narrative"
              />
            </label>
          </div>
        </Modal>
      )}

      {modal === "supersede" && (
        <ConfirmModal
          open
          title="Supersede this audit"
          confirmLabel="Supersede and open a correction"
          message={
            <>
              This audit is retired and a fresh draft opens in its place,
              pointing back at it. The original stays exactly as the vendor saw
              it — that is the point: their comments must keep referring to a
              document that still says what they replied to.
            </>
          }
          onCancel={closeModal}
          onConfirm={() => void onSupersede()}
        />
      )}

      {modal === "quote" && (
        <Modal
          open
          onClose={closeModal}
          title={quoteForm.id ? "Edit competitor" : "Add competitor"}
          description="Free text throughout — one competitor quotes a flat fee, the next a percentage, the third bundles services Phoenix does not buy. You read them and write the finding."
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={
                  !quoteForm.competitorName.trim() ||
                  addQuote.isPending ||
                  updateQuote.isPending
                }
                onClick={() => void onSubmitQuote()}
                data-testid="vendor-audit-quote-submit"
              >
                {quoteForm.id ? "Save competitor" : "Add competitor"}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-space-3">
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Competitor name (required)
              </span>
              <Input
                value={quoteForm.competitorName}
                onChange={(e) =>
                  setQuoteForm({ ...quoteForm, competitorName: e.target.value })
                }
                data-testid="vendor-audit-quote-name"
              />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-3">
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">Contact</span>
                <Input
                  value={quoteForm.contactName}
                  onChange={(e) =>
                    setQuoteForm({ ...quoteForm, contactName: e.target.value })
                  }
                  data-testid="vendor-audit-quote-contact"
                />
              </label>
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">How reached</span>
                <Input
                  value={quoteForm.contactMethod}
                  onChange={(e) =>
                    setQuoteForm({ ...quoteForm, contactMethod: e.target.value })
                  }
                  placeholder="Phone, email, web form"
                  data-testid="vendor-audit-quote-method"
                />
              </label>
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">Contacted on</span>
                <Input
                  type="date"
                  value={quoteForm.contactedOn}
                  onChange={(e) =>
                    setQuoteForm({ ...quoteForm, contactedOn: e.target.value })
                  }
                  data-testid="vendor-audit-quote-date"
                />
              </label>
            </div>
            <label className="flex items-start gap-space-2 text-small">
              <input
                type="checkbox"
                className="mt-space-1"
                checked={quoteForm.declined}
                onChange={(e) =>
                  setQuoteForm({ ...quoteForm, declined: e.target.checked })
                }
                data-testid="vendor-audit-quote-declined"
              />
              <span>
                They were approached but would not quote.
                <span className="block text-caption text-gray-mid">
                  A declined competitor is a result, not a blank row — record
                  them rather than leaving them out.
                </span>
              </span>
            </label>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">Quoted terms</span>
              <Textarea
                rows={3}
                value={quoteForm.quotedTerms}
                onChange={(e) =>
                  setQuoteForm({ ...quoteForm, quotedTerms: e.target.value })
                }
                data-testid="vendor-audit-quote-terms"
              />
            </label>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Services offered
              </span>
              <Textarea
                rows={3}
                value={quoteForm.servicesOffered}
                onChange={(e) =>
                  setQuoteForm({ ...quoteForm, servicesOffered: e.target.value })
                }
                data-testid="vendor-audit-quote-services"
              />
            </label>
          </div>
        </Modal>
      )}

      {modal === "remove-quote" && quoteToRemove && (
        <ConfirmModal
          open
          destructive
          title="Remove this competitor"
          confirmLabel="Remove"
          message={
            <>
              {quoteToRemove.competitorName} is removed from this audit. If they
              were approached and declined, keep the row and tick “would not
              quote” instead — that is evidence, not noise.
            </>
          }
          onCancel={closeModal}
          onConfirm={() =>
            void run(
              () => removeQuote.mutateAsync(quoteToRemove.id),
              "Competitor quote removed.",
            )
          }
        />
      )}
    </ResourceDrawer>
  );
}

function DraftField({
  label,
  value,
  onChange,
  disabled,
  testId,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
  testId: string;
}) {
  return (
    <label className="flex flex-col gap-space-1 text-caption">
      <span className="text-gray-mid uppercase tracking-label">{label}</span>
      <Textarea
        rows={3}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </label>
  );
}

function ReadField({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-caption text-gray-mid uppercase tracking-label">{label}</dt>
      <dd className="text-small m-0 mt-space-1 whitespace-pre-wrap">
        {value?.trim() ? (
          value
        ) : (
          <span className="text-gray-mid italic">Not recorded</span>
        )}
      </dd>
    </div>
  );
}
